import { useLocation, useParams } from "react-router-dom";

import type { TreatmentOverview } from "../hooks/useTreatmentOverviews";

import { TreatmentDetailContent } from "./TreatmentDetailContent";

/**
 * Prescription detail page at `/care/treatment/:treatmentCode`.
 *
 * Two entry paths:
 *   1. In-app navigation from `CareTabPage` — the treatment object is
 *      passed via `location.state` so we render instantly while subscribing
 *      to Apollo's cached query and refreshing it when the WebView returns
 *      to focus.
 *   2. Deeplink / browser refresh — no preloaded `state`, so the page
 *      falls through to `useTreatmentOverviewByCode` which fetches the
 *      list once and finds the matching treatment.
 *
 * The page is split across this folder:
 *   - `TreatmentDetailPage` (outer)        : reads route params + state,
 *                                            keys the inner by `treatmentCode`
 *                                            so a `/care/treatment/A` →
 *                                            `/care/treatment/B` navigation
 *                                            forces a fresh mount (analytics
 *                                            hook must re-fire for the new
 *                                            medication).
 *   - `TreatmentDetailContent` (router)    : owns the treatment query and
 *                                            focus refresh, preferring fresh
 *                                            query data over preloaded state.
 *   - `TreatmentDetailDeeplinkLoader`      : renders loading / error /
 *                                            not-found when no treatment is
 *                                            available.
 *   - `TreatmentDetailReadyContent` (leaf) : receives a definite treatment,
 *                                            fires
 *                                            `your_prescription_webview_viewed`
 *                                            with the correct `medicationName`,
 *                                            renders the full UI.
 *   - `DetailShell`                        : shared shell + page header used
 *                                            by every state above.
 *
 * Layout matches the "Your prescription" design at a structural level
 * (back header, med name, product lockup, med-log CTAs when logging,
 * action-link list with row dividers, pill-shaped chat-with-doctor CTA).
 */

type TreatmentDetailLocationState = { treatment: TreatmentOverview };

const isTreatmentDetailLocationState = (
  state: unknown
): state is TreatmentDetailLocationState =>
  typeof state === "object" &&
  state !== null &&
  "treatment" in state &&
  typeof (state as { treatment: unknown }).treatment === "object" &&
  (state as { treatment: unknown }).treatment !== null;

export const TreatmentDetailPage = () => {
  const { treatmentCode: rawTreatmentCode } = useParams<{
    treatmentCode: string;
  }>();
  const treatmentCode = rawTreatmentCode
    ? decodeURIComponent(rawTreatmentCode)
    : undefined;
  const { state } = useLocation();
  const preloadedTreatment = isTreatmentDetailLocationState(state)
    ? state.treatment
    : undefined;

  return (
    <TreatmentDetailContent
      key={treatmentCode ?? ""}
      treatmentCode={treatmentCode}
      preloadedTreatment={preloadedTreatment}
    />
  );
};
